import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Left-anchored drawer. Slides in from the screen edge, 88% width, with a
 * 60% black scrim. Used for tenant switcher + account links + secondary
 * navigation that doesn't belong in the bottom tab bar.
 */
interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  children: ReactNode;
}

export function Drawer({ open, onOpenChange, title, children }: DrawerProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.aside
                className="fixed inset-y-0 left-0 z-50 flex h-full w-[86%] max-w-[340px] flex-col overflow-hidden rounded-r-[1.125rem] border-0 bg-paper shadow-2xl shadow-black/30 outline-none ring-0"
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
              >
                {title ? (
                  <Dialog.Title asChild>
                    <h2 className="px-6 pt-safe-or-6 pb-4 font-display text-display-lg text-ink">
                      {title}
                    </h2>
                  </Dialog.Title>
                ) : (
                  <Dialog.Title className="sr-only">Menu</Dialog.Title>
                )}
                <div className="flex min-h-0 flex-1 flex-col">{children}</div>
              </motion.aside>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}
